// frontend/src/components/assessment/panels/ArtifactPanel.js
// Right panel: tab bar + goals form (step 2) + case study form (step 3) + script editor (step 4)

import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import CaseStudyForm from '../artifacts/CaseStudyForm';
import NarrationScript from '../artifacts/NarrationScript';
import InnerMonologueScript from '../artifacts/InnerMonologueScript';
import AssessmentReport from '../artifacts/AssessmentReport';
import { useFeature } from '../../../hooks/useFeature';
import { EXAMPLE_SCRIPT } from '../../../data/exampleScript';

const TABS = [
  { id: 'caseStudy', label: 'Situation' },
  { id: 'script',    label: 'Script' },
  { id: 'report',    label: 'Debrief' },
];

const formatPhaseLabel = (phase, subphase) => {
  if (!phase) return null;
  const fmt = s => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return subphase ? `${fmt(phase)} · ${fmt(subphase)}` : fmt(phase);
};

// Maps current phase to which artifact tab it "owns"
const PHASE_TO_ARTIFACT = {
  case_study:      'caseStudy',
  narration:       'script',
  goals:           'caseStudy',
  inner_monologue: 'script',
  reporting:       'report',
};

// Phase-specific help text shown in the popover
const PHASE_HELP = {
  situation: 'Tell Scarlett about the conversation you want to work on.',
  case_study:
    "I need a few bits of information about this situation.  I can ask you about it, you can fill in the information in the form on the right hand side of the screen, or both. " +
    "\nWhen all is in place, you can either tell me you’re done, or you click on the 'Done' button on the upper right.",
  narration:
    "Write out how this conversation went — or how you'd like it to go. " +
    '\n• You can type directly into the turns, or — if your browser supports it — click the microphone icon next to each line of dialog to dictate. ' +
    '\n• You move dialog using the arrow icons on the right, or insert a new turn using the + button. ' +
    '\n• You need at least two complete exchanges before you can move on.',
  goals:
    'Tell Scarlett what you hope to learn from this workshop case study.',
  inner_monologue:
    "Your inner monologue captures what you're thinking during the conversation. " +
    "\n\n• For each turn, write what's going through your mind, especially those thoughts that differ from your words. " +
    "\n• Click any 'thoughts:' field to type directly into the turns, or — if your browser supports it — click the microphone icon next to each line of dialog to dictate." +
    "\n• Use the '×' button to clear a thought (you'll get a 5-second undo prompt). " +
    "\n• The conversation itself is now read-only. ",
  reporting: "Your report is ready. Review your results below. " +
  "\n\nYou can ask Scarlett to clarify or expand on any part of the report — just use the chat as usual.",
};

// Shown instead of PHASE_HELP.reporting when assessments_view is off (no Debrief to review)
const REPORTING_HELP_LOCKED = "Your case study has been submitted.  You'll be discussing it in the workshop.  Thank you!";

// Message sent to the AI when the Done button is clicked
const PHASE_DONE_MESSAGE = {
  case_study:      "I've finished filling out the case study",
  narration:       "I'm done transcribing the conversation",
  inner_monologue: "I've finished my inner thoughts and feelings",
};

// ─────────────────────────────────────────────────────────────────────────────
// Completion checks — determine whether the Done button should be visible
// ─────────────────────────────────────────────────────────────────────────────

function isCaseStudyComplete(session) {
  const cs = session?.caseStudy;
  if (!cs) return false;
  return [
    cs.characterBrief?.inSum?.content,
    cs.realIssue?.summary?.content,
    cs.realIssue?.dataAndReasoning?.content,
    cs.characterStakes?.downSides?.inSum?.content,
    cs.characterStakes?.upSides?.inSum?.content,
    cs.challenge?.content,
  ].every(v => v && v.trim().length > 0);
}

function isScriptComplete(session) {
  const pairs = session?.script ?? [];
  return pairs.filter(p => p.me?.content && p.me.content.trim().length > 0).length >= 2;
}

function isDoneReady(phase, session) {
  switch (phase) {
    case 'case_study':      return isCaseStudyComplete(session);
    case 'narration':
    case 'inner_monologue': return isScriptComplete(session);
    default:                return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Example modal — static, read-only rendering of EXAMPLE_SCRIPT
// ─────────────────────────────────────────────────────────────────────────────

const ExampleTurnRow = ({ speaker, content, thought, showThought }) => (
  <div className="im-turn-row">
    {showThought && (
      <div className="im-thought-col">
        <div className="im-label-col">
          <span className="script-speaker-label im-thought-label">thoughts:</span>
        </div>
        <div className="im-thought-cell">
          <div className="im-readonly-content im-thought-readonly-text">
            {thought || <span className="im-readonly-empty">—</span>}
          </div>
        </div>
      </div>
    )}
    <div className={`im-turn-col im-turn-${speaker}`}>
      <span className={`script-speaker-label script-speaker-${speaker}`}>{speaker}:</span>
      <div className="im-readonly-content">
        {content || <span className="im-readonly-empty">—</span>}
      </div>
    </div>
  </div>
);

ExampleTurnRow.propTypes = {
  speaker:     PropTypes.oneOf(['me', 'them']).isRequired,
  content:     PropTypes.string,
  thought:     PropTypes.string,
  showThought: PropTypes.bool,
};

const ExampleScriptPair = ({ pair, showThoughts }) => (
  <div className="im-pair">
    <ExampleTurnRow
      speaker="me"
      content={pair.me?.content}
      thought={pair.me_thought?.content}
      showThought={showThoughts}
    />
    <ExampleTurnRow
      speaker="them"
      content={pair.them?.content}
      thought={pair.them_thought?.content}
      showThought={showThoughts}
    />
  </div>
);

ExampleScriptPair.propTypes = {
  pair:         PropTypes.object.isRequired,
  showThoughts: PropTypes.bool,
};

// ─────────────────────────────────────────────────────────────────────────────
// ArtifactPanel
// ─────────────────────────────────────────────────────────────────────────────

const ArtifactPanel = forwardRef(({
  activeTab,
  onTabChange,
  session,
  sessionLoading,
  onCaseStudyFieldChange,
  onGoalsFieldChange,
  onScriptTurnChange,
  onScriptReplace,
  onDone,
  conversationId,
  reportFailure,
  reportBodyError,
  onRetryReportBody,
}, ref) => {
  const [saveStatus,      setSaveStatus]      = useState(null);
  const [showHelp,        setShowHelp]        = useState(false);
  const [imCompleteness,  setImCompleteness]  = useState(null);
  const [showExample,     setShowExample]     = useState(false);
  const helpWrapperRef = useRef(null);

  // Ref to the editor currently rendered (case study / narration / inner monologue).
  // Whichever is mounted receives this ref and exposes flush(); the report has none.
  // AssessmentView calls artifactRef.flush() before an AI turn to serialize the writers.
  const activeEditorRef = useRef(null);
  useImperativeHandle(ref, () => ({
    flush: async () => {
      try { await activeEditorRef.current?.flush?.(); }
      catch (err) { console.warn('[ArtifactPanel] flush before send failed (continuing):', err); }
    },
  }), []);

  const { enabled: assessmentsViewEnabled } = useFeature('assessments_view');

  // Situation/Script become permanently read-only once reporting starts —
  // unconditional on the flag, which only controls Debrief visibility.
  const locked = session?.currentPhase === 'reporting';

  // Situation locks a phase earlier than the rest: once inner_monologue begins,
  // the case study is done being edited, mirroring how the narration text itself
  // (the me:/them: turns) is already permanently read-only inside InnerMonologueScript.
  const situationLocked = locked || session?.currentPhase === 'inner_monologue';

  const visibleTabs = TABS.filter(tab =>
    session?.seenTabs?.includes(tab.id) &&
    (tab.id !== 'report' || assessmentsViewEnabled)
  );

  // If focus is on 'report' while the Debrief is hidden (e.g. session was
  // hydrated while the flag was on, or the phase just flipped to 'reporting'),
  // fall back to the last visible tab instead of rendering nothing.
  const effectiveActiveTab = (activeTab === 'report' && !assessmentsViewEnabled)
    ? (visibleTabs[visibleTabs.length - 1]?.id ?? null)
    : activeTab;

  useEffect(() => {
    if (!showHelp) return;
    const handler = (e) => {
      if (helpWrapperRef.current && !helpWrapperRef.current.contains(e.target)) {
        setShowHelp(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showHelp]);

  const currentPhase   = session?.currentPhase;
  const phaseArtifact  = PHASE_TO_ARTIFACT[currentPhase];
  const helpText       = currentPhase === 'reporting' && !assessmentsViewEnabled
    ? REPORTING_HELP_LOCKED
    : (PHASE_HELP[currentPhase] ?? null);
  const doneMessage    = PHASE_DONE_MESSAGE[currentPhase] ?? null;

  const perspectiveGapBlocked = currentPhase === 'inner_monologue' &&
    imCompleteness?.minimumMet &&
    (imCompleteness?.meThoughtsFilled === 0 || imCompleteness?.themThoughtsFilled === 0);

  const showDoneButton = doneMessage && activeTab === phaseArtifact && isDoneReady(currentPhase, session)
    && !perspectiveGapBlocked
    && (currentPhase !== 'inner_monologue' || (imCompleteness?.filledSlots ?? 0) >= 3);

  const showExampleButton  = effectiveActiveTab === 'script' && phaseArtifact === 'script';
  const showExampleThoughts = currentPhase === 'inner_monologue';

  const handleDone = () => {
    if (!doneMessage) return;
    const message = session?.currentSubphase === 'confirmation'
      ? "Yes, I'm ready to move on."
      : doneMessage;
    onDone(message);
  };

  return (
    <div className="artifact-panel">
      <div className="artifact-tab-bar">
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            className={`artifact-tab${effectiveActiveTab === tab.id ? ' active' : ''}`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        <div className="artifact-tab-bar-right">
          {saveStatus && (
            <span className="artifact-save-status">
              {saveStatus === 'saving' ? 'Saving…' : 'Saved'}
            </span>
          )}
          {showExampleButton && (
            <button className="artifact-example-btn" onClick={() => setShowExample(true)}>Example</button>
          )}
          {helpText && (
            <div className="script-help-wrapper" ref={helpWrapperRef}>
              <button
                className="script-help-btn"
                onClick={() => setShowHelp(v => !v)}
                aria-label="Help"
              >?</button>
              {showHelp && (
                <div className="script-help-popover">
                  {helpText}
                  <div className="script-help-phase-label">
                    {formatPhaseLabel(session?.currentPhase, session?.currentSubphase)}
                  </div>
                </div>
              )}
            </div>
          )}
          {showDoneButton && (
            <button className="script-done-btn" onClick={handleDone}>Done</button>
          )}
        </div>
      </div>

      <div className="artifact-tab-content">
        {effectiveActiveTab === 'caseStudy' && (
          sessionLoading ? (
            <div className="artifact-placeholder"><p>Loading…</p></div>
          ) : (
            <CaseStudyForm
              ref={activeEditorRef}
              caseStudy={session?.caseStudy}
              userGoals={session?.userGoals}
              conversationId={conversationId}
              onFieldChange={onCaseStudyFieldChange}
              onGoalsFieldChange={onGoalsFieldChange}
              onSaveStatusChange={setSaveStatus}
              readOnly={situationLocked}
            />
          )
        )}
        {effectiveActiveTab === 'script' && (
          sessionLoading ? (
            <div className="artifact-placeholder"><p>Loading…</p></div>
          ) : (session?.currentPhase === 'inner_monologue' || session?.currentPhase === 'reporting') ? (
            <InnerMonologueScript
              ref={activeEditorRef}
              script={session?.script ?? []}
              conversationId={conversationId}
              onClientThoughtChange={onScriptTurnChange}
              onSaveStatusChange={setSaveStatus}
              onCompletenessChange={setImCompleteness}
              readOnly={locked}
            />
          ) : (
            <NarrationScript
              ref={activeEditorRef}
              script={session?.script ?? []}
              conversationId={conversationId}
              onTurnChange={onScriptTurnChange}
              onScriptReplace={onScriptReplace}
              onSaveStatusChange={setSaveStatus}
            />
          )
        )}
        {effectiveActiveTab === 'report' && (
          <AssessmentReport
            report={session?.report}
            currentSubphase={session?.currentSubphase}
            reportFailure={reportFailure}
            reportBodyError={reportBodyError}
            onRetryReportBody={onRetryReportBody}
          />
        )}
      </div>

      {showExample && (
        <div
          className="modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowExample(false); }}
        >
          <div className="modal-content artifact-example-modal">
            <div className="modal-header">
              <h3 className="modal-title">Example</h3>
              <button
                onClick={() => setShowExample(false)}
                className="modal-close"
                aria-label="Close"
              >×</button>
            </div>
            <div className="artifact-example-body">
              {EXAMPLE_SCRIPT.map(pair => (
                <ExampleScriptPair key={pair.pairId} pair={pair} showThoughts={showExampleThoughts} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ArtifactPanel.displayName = 'ArtifactPanel';

ArtifactPanel.propTypes = {
  activeTab:              PropTypes.oneOf(['caseStudy', 'script', 'report']).isRequired,
  onTabChange:            PropTypes.func.isRequired,
  session:                PropTypes.object,
  sessionLoading:         PropTypes.bool,
  onCaseStudyFieldChange: PropTypes.func,
  onGoalsFieldChange:     PropTypes.func,
  onScriptTurnChange:     PropTypes.func,
  onScriptReplace:        PropTypes.func,
  onDone:                 PropTypes.func,
  conversationId:         PropTypes.string,
  reportFailure:          PropTypes.shape({ reason: PropTypes.string }),
  reportBodyError:        PropTypes.bool,
  onRetryReportBody:      PropTypes.func,
};

export default ArtifactPanel;